import React, {useEffect, useState} from 'react'
//components
import { Button } from '@components/ui/buttons/Button'
import EditableTable from '@components/ui/editableTable/EditableTable'
import Label from '@components/ui/labels/Label'
import { SelectContainer } from '@components/ui/layout/SelectContainer'
import Select from "react-select"
import { SpeedApprovalTable } from '@components/tables/SpeedApprovalTable'
//table config
import { SpeedNotificationSchema } from '../tableConfig/jobControlSchemas'
//hooks
import { useLoading } from '@contexts/LoadingContext'
import useRunOptions from '@hooks/useRunOptions'
import useUserOptions  from "@hooks/useUserOptions"
import { useError } from '@contexts/ErrorContext';
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { useSuccess } from '@contexts/SuccessContext'
import { useSpeedApprovals } from "@hooks/useSpeedApprovals";
//api, util functions
import { 
    getSpeedNotificationsAPI,
    createSpeedNotificationAPI,
    updateSpeedNotificationAPI,
    deleteSpeedNotificationAPI
} from "@services/notificationsAPI"
import { areObjectsEqual } from "@utils/areObjectsEqual"
import { useUser } from '@contexts/UserContext'

/**
 *     
 * Speed notifications
    - select a run
    - show multi-select for users that can be assigned to receive notifications for run
    - show table with users, have toggles for email and phone

 * @param {DropdownOption} selectedJob
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 */
export const SpeedNotifications = ({selectedJob}) => {
    //set state
    const [selectedRun, setSelectedRun] = useState(null)
    const [selectedUsers, setSelectedUsers] = useState(null)

    const [notificationData, setNotificationData] = useState([])
    const [notificationDataOriginal, setNotificationDataOriginal] = useState([]) //use to compare original and changed data when time to submit
  
    const { showLoading, hideLoading } = useLoading();
    const { runNameOptions, fetchRuns } = useRunOptions()
    const { userNameOptions, fetchUsersForSpeedNotifications} = useUserOptions()
    const {showError} = useError()
    const {setHasUnsavedChanges} = useUnsavedChanges()
    const { timeoutSuccess } = useSuccess()
    const {setStoredJob} = useUser()

    const { speedApprovals, fetchSpeedApprovals, approve, reject } = useSpeedApprovals(selectedJob, selectedRun);

    //fetch notifications function
    const fetchNotifications = async() => {
        showLoading("Getting notification data...")
        
        const response = await getSpeedNotificationsAPI(selectedRun.value);

        //if error, reset state
        if (!response.success) {
            hideLoading()

            //show message
            showError(`Error getting speed notifications: ${response.message}`)

            //reset state
            setSelectedUsers(null)
            setNotificationData([])
            setNotificationDataOriginal([])
            return
        }
                
        //get user name by comparing userNameOptions with UserID
        const getUserName = (UserID) => {
            
            const user = userNameOptions.find(user => user.value === UserID)
            
            return user?.label ?? ""
        }

        //if there are notifications, format them to fill table
        if(response.data.length > 0){

            //filter out any notifications that are not speed notifications
            //speed notifications have a NTID of 1
            const speedNotifications = response.data.filter(notification => notification.NTID === 1)

            const formattedData = speedNotifications.map(notification => {
                return {
                    "Name": getUserName(notification.SendTo),
                    "IsEmail": notification.IsEmail,
                    "IsPhone": notification.IsPhone,
                    "UserID": notification.SendTo,
                    "NID": notification.NID,
                    "AltEmail": notification.AltEmail,
                    "AltPhone": notification.AltPhone
                }
            })
    
            //set selectedUsers to those that have notifications set for this run
            const usersToNotify = formattedData.map(notification => {
                return {
                    value: notification.UserID,
                    label: notification.Name
                }
            })
    
            setSelectedUsers(usersToNotify)
    
            //set data for table
            setNotificationData(formattedData)
            setNotificationDataOriginal(formattedData)

            hideLoading()

            return
        }

        //if no users to notify, let user know and reset state
        setSelectedUsers(null)
    
        setNotificationData([])
        setNotificationDataOriginal([])

        hideLoading()
    }

    //fetch runs when component loads or if job selected/changed
    useEffect(() => {
        
        if(!selectedJob) return 

        fetchRuns(selectedJob.value)
        fetchUsersForSpeedNotifications()

        //if job changes, reset runs and clear notifications
        setSelectedRun(null)
        setSelectedUsers(null)
        setNotificationData([])
        setNotificationDataOriginal([])

    }, [selectedJob]) //eslint-disable-line

    //get speed notifications when run selected
    useEffect(() => {

        //if no run selected, reset state
        if(!selectedRun){
            setNotificationData([])
            setNotificationDataOriginal([])
            setSelectedUsers(null)
            return
        }  

        fetchNotifications(selectedRun.value)
        fetchSpeedApprovals()

    }, [selectedRun]) //eslint-disable-line

    //update table data (notificationData) when selectedUsers changes
    useEffect(() => {
       
        if(!selectedUsers) return

        setNotificationData(prevData => {
            // copy data
            let newData = [...prevData];
        
            // get userIds from the previous notification data
            const prevUserIds = prevData.map(data => data.UserID);
        
            // get newly selected users and removed users
            const addedUsers = selectedUsers.filter(
              user => !prevUserIds.includes(user.value)
            );
        
            const removedUsers = prevUserIds.filter(
              userId => !selectedUsers.some(user => user.value === userId)
            );

            //if there were changes, update hasUnsavedChanges state
            if(addedUsers.length > 0 || removedUsers.length > 0) {
                setHasUnsavedChanges(true)
            }
        
            // update notificationData with added and removed users
            addedUsers.forEach(user => {
              newData.push({
                UserID: user.value,
                Name: user.label,
                IsEmail: false,
                IsPhone: false,
                AltEmail: null,
                AltPhone: null
              });
            });
        
            removedUsers.forEach(userId => {
              newData = newData.filter(data => data.UserID !== userId);
            });
        
            return newData;
          });

          

    }, [selectedUsers]); //eslint-disable-line
   
    //submit function - handles added, updated, and removed notifications

    const handleSubmit = async() =>{
        //set loading message, remove old submit error message if exists
        showLoading("Submitting...")

        //create copy of data that can be updated with new notification IDs
        const updatedData = [...notificationData]; 

        for (let i = 0; i < updatedData.length; i++) {
            const notification = updatedData[i]

            //UPDATE NOTIFICATIONS - notifications that are not new (have a NID) and are different from the original data (to reduce api calls)
            if(notification.hasOwnProperty("NID")){

                //compare to original data to see if notification data has changed
                //if it has, update the data
                const originalRow = notificationDataOriginal.find(original => original.NID === notification.NID)

                if(!areObjectsEqual(notification, originalRow)){
                    const updatedNotification = {
                        ...notification,
                        JobID: selectedJob.value
                    }
                    
                    const response = await updateSpeedNotificationAPI(notification.NID, updatedNotification)

                    //if not successful, update error message and return
                    if(!response.success){
                        hideLoading()

                        showError(`Error updating speed notifications: ${response.message}`)
    
                        return
                    }
                }

            } else {

                //CREATE NOTIFICATIONS - if no NID, it's a new notification, so create it
                const newNotification = {
                    ...notification,
                    JobID: selectedJob.value,
                }

                const response = await createSpeedNotificationAPI(selectedRun.value, newNotification)

                //if not successful, update error message and return
                if(!response.success){
                    hideLoading()

                    showError(`Error creating speed notifications: ${response.message}`)

                    return
                }

                //once created, add NID to notification data
                updatedData[i] = {
                    ...notification,
                    NID: response.data.NID
                };
                
           }
           
        }

        // DELETE NOTIFICATIONS - find notifications that are in notificationDataOriginal but not in notificationData
        const notificationsToDelete = notificationDataOriginal.filter(original =>
            !notificationData.some(notification => notification.NID === original.NID)
        );

        for (let i = 0; i < notificationsToDelete.length; i++) {
            const notification = notificationsToDelete[i];
            const response = await deleteSpeedNotificationAPI(notification.NID);

            // if not successful, update error message and return
            if (!response.success) {
                hideLoading()

                showError(`Error deleting speed notifications: ${response.message}`)

                return;
            }
        }
        
        //reset state with current data
        setNotificationData(updatedData)
        setNotificationDataOriginal(updatedData)

        setStoredJob(prev => ({
            ...prev,
            notificationCount: {
            ...prev.notificationCount,
            [selectedRun.value]: {
                ...prev.notificationCount[selectedRun.value],
                SpeedNotificationCount: updatedData.length
            }
            }
        }));

        //let user know that data submitted successfully
        hideLoading()

        setHasUnsavedChanges(false)

        timeoutSuccess("Saved")
    }

   
    return (
        <div className='martrak-max-width'>

            {selectedJob ? 
            (
                <>

                    {/* run dropdown */}
                    <Label htmlFor='runDropdown'>Select run</Label>

                    <SelectContainer>
                        <Select
                            value={selectedRun}
                            options={runNameOptions}
                            name="runDropdown"
                            onChange={(x) => setSelectedRun(x)}
                            menuPosition='fixed'
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </SelectContainer>


                    {/* multiselect to add/remove from job */}
                    { selectedRun ?
                      (
                        <>
                            <hr className='my-12'/>

                            <Label htmlFor='usersToNotify'>Users Who Can Approve Speed Notifications</Label>

                            {/* select users to be notified */}
                            <SelectContainer>
                                <Select
                                    value={selectedUsers}
                                    options={userNameOptions}
                                    name="usersToNotify"
                                    onChange={(x) => setSelectedUsers(x)}
                                    menuPosition='fixed'
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    isMulti={true}
                                />
                            </SelectContainer>

                            {/* user table - fills in when user changes selections */}

                            {selectedUsers && selectedUsers.length > 0 
                                ? 
                                    <>
                                        <EditableTable 
                                            data={notificationData} 
                                            setData={setNotificationData} 
                                            schema={SpeedNotificationSchema} 
                                            canDeleteRow={false} 
                                        />
                                        
                                    </>

                                : null
                            }
                            
                            <Button variant='success' onClick={() => handleSubmit()}>Submit Users</Button>

                            {/* approval table - shows high/low speeds that are approved or pending */}
                            {speedApprovals.length > 0 ? (
                                <>
                                    <hr className='my-12'/>

                                    <Label htmlFor='usersToNotify'>Pending/Approved Speeds</Label>

                                    <SpeedApprovalTable
                                        rows={speedApprovals}
                                        onApprove={approve}
                                        onReject={reject}
                                        timezone={selectedJob?.timezone}
                                    />
                                </>
                            ) : null
                            }

                        </>

                    ) : null
                    }

                </>
            ) 
            : 

            (
                <div className='flex justify-center mt-10'>
                    <p className='text-purple-600 font-bold italic'>Please select a job.</p>
                </div>
            )

            }
            
        </div>
    )
}